"use client";

import { c as _c } from "react/compiler-runtime";
import { ImageIcon, Loader2Icon, MicIcon, Music2Icon, PaperclipIcon, PlusIcon, SendIcon, SquareIcon, XIcon } from "lucide-react";
import { nanoid } from "nanoid";
import { type ChangeEvent, type ChangeEventHandler, Children, type ClipboardEventHandler, type ComponentProps, createContext, type FormEvent, type FormEventHandler, Fragment, type HTMLAttributes, type KeyboardEventHandler, type PropsWithChildren, type ReactNode, type RefObject, useCallback, use, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from "@/components/ui/input-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { ChatStatus, FileUIPart } from "ai";
// ============================================================================
// Provider Context & Types
// ============================================================================

export interface AttachmentsContext {
  files: (FileUIPart & {
    id: string;
  })[];
  add: (files: File[] | FileList) => void;
  remove: (id: string) => void;
  clear: () => void;
  openFileDialog: () => void;
  fileInputRef: RefObject<HTMLInputElement | null>;
}
export interface TextInputContext {
  value: string;
  setInput: (v: string) => void;
  clear: () => void;
}
export interface PromptInputController {
  textInput: TextInputContext;
  attachments: AttachmentsContext;
  /** INTERNAL: Allows PromptInput to register its file textInput + "open" callback */
  __registerFileInput: (ref: RefObject<HTMLInputElement | null>, open: () => void) => void;
}
const PromptInputContext = createContext<PromptInputController | null>(null);
// Local attachments context used by both provider and local mode
const LocalAttachmentsContext = createContext<AttachmentsContext | null>(null);
export const usePromptInputController = () => {
  const ctx = use(PromptInputContext);
  if (!ctx) {
    throw new Error("Wrap your component inside <PromptInputProvider> to use usePromptInputController().");
  }
  return ctx;
};

// Optional variants (do NOT throw). Useful for dual-mode components.
const optional_usePromptInputController = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return use(PromptInputContext);
};

// (Removed provider-specific attachments hooks; unified on LocalAttachmentsContext)

export type PromptInputProviderProps = PropsWithChildren<{
  initialInput?: string;
}>;

/**
 * Optional global provider that lifts PromptInput state outside of PromptInput.
 * If you don't use it, PromptInput stays fully self-managed.
 */
export function PromptInputProvider(t0) {
  const $ = _c(20);
  const {
    initialInput: t1,
    children
  } = t0;
  const initialTextInput = t1 === undefined ? "" : t1;
  const [textInput, setTextInput] = useState(initialTextInput);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setTextInput("");
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const clearInput = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [attachements, setAttachements] = useState(t3);
  const fileInputRef = useRef(null);
  const openRef = useRef(_temp);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = files => {
      const incoming = Array.from(files);
      if (incoming.length === 0) {
        return;
      }
      setAttachements(prev => prev.concat(incoming.map(_temp2)));
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const add = t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = id => {
      setAttachements(prev_0 => {
        const found = prev_0.find(f => f.id === id);
        if (found?.url) {
          URL.revokeObjectURL(found.url);
        }
        return prev_0.filter(f_0 => f_0.id !== id);
      });
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const remove = t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setAttachements(_temp3);
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const clear = t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      openRef.current?.();
    };
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const openFileDialog = t7;
  let t8;
  if ($[6] !== attachements) {
    t8 = {
      files: attachements,
      add,
      remove,
      clear,
      openFileDialog,
      fileInputRef
    };
    $[6] = attachements;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const attachments = t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (ref, open) => {
      fileInputRef.current = ref.current;
      openRef.current = open;
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const __registerFileInput = t9;
  let t10;
  if ($[9] !== textInput) {
    t10 = {
      value: textInput,
      setInput: setTextInput,
      clear: clearInput
    };
    $[9] = textInput;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== attachments || $[12] !== t10) {
    t11 = {
      textInput: t10,
      attachments,
      __registerFileInput
    };
    $[11] = attachments;
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const controller = t11;
  let t12;
  if ($[14] !== attachments || $[15] !== children) {
    t12 = <LocalAttachmentsContext value={attachments}>{children}</LocalAttachmentsContext>;
    $[14] = attachments;
    $[15] = children;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== controller || $[18] !== t12) {
    t13 = <PromptInputContext value={controller}>{t12}</PromptInputContext>;
    $[17] = controller;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  return t13;
}

// ============================================================================
// Component Context & Hooks
// ============================================================================
function _temp3(prev_1) {
  for (const f_1 of prev_1) {
    if (f_1.url) {
      URL.revokeObjectURL(f_1.url);
    }
  }
  return [];
}
function _temp2(file) {
  return {
    id: nanoid(),
    type: "file" as const,
    url: URL.createObjectURL(file),
    mediaType: file.type,
    filename: file.name
  };
}
function _temp() {}
export const usePromptInputAttachments = () => {
  const context = use(LocalAttachmentsContext);
  if (!context) {
    throw new Error("usePromptInputAttachments must be used within a PromptInput or PromptInputProvider");
  }
  return context;
};
export type PromptInputAttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: FileUIPart & {
    id: string;
  };
  className?: string;
};
export function PromptInputAttachment(t0) {
  const $ = _c(38);
  let className;
  let data;
  let props;
  if ($[0] !== t0) {
    ({
      data,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = data;
    $[3] = props;
  } else {
    className = $[1];
    data = $[2];
    props = $[3];
  }
  const attachments = usePromptInputAttachments();
  const mediaType = data.mediaType?.startsWith("image/") && data.url ? "image" : data.mediaType?.startsWith("audio/") && data.url ? "audio" : "file";
  if (mediaType === "audio") {
    let t1;
    if ($[4] !== className) {
      t1 = cn("group relative flex h-8 cursor-pointer select-none items-center gap-1.5 rounded-md border border-border px-1.5 font-medium text-sm transition-all hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50", className);
      $[4] = className;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex size-5 shrink-0 items-center justify-center overflow-hidden rounded bg-background"><Music2Icon className="size-3 text-muted-foreground" /></div>;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== attachments || $[8] !== data.id) {
      t3 = e => {
        e.stopPropagation();
        attachments.remove(data.id);
      };
      $[7] = attachments;
      $[8] = data.id;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <XIcon />;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== t3) {
      t5 = <Button aria-label="Remove attachment" className="size-5 rounded p-0 opacity-0 transition-opacity group-hover:opacity-100 [&>svg]:size-2.5" onClick={t3} type="button" variant="ghost">{t4}</Button>;
      $[11] = t3;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== data.id || $[14] !== props || $[15] !== t1 || $[16] !== t5) {
      t6 = <div className={t1} key={data.id} {...props}>{t2}{t5}</div>;
      $[13] = data.id;
      $[14] = props;
      $[15] = t1;
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    return t6;
  }
  const t1 = mediaType === "image" ? "h-14 w-14" : "h-8 w-auto max-w-full";
  let t2;
  if ($[18] !== className || $[19] !== t1) {
    t2 = cn("group relative rounded-md border", className, t1);
    $[18] = className;
    $[19] = t1;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  let t3;
  if ($[21] !== data.filename || $[22] !== data.mediaType || $[23] !== data.url || $[24] !== mediaType) {
    t3 = mediaType === "image" ? <img alt={data.filename ?? "attachment"} className="size-full rounded-md object-cover" height={56} src={data.url} width={56} /> : <div className="flex size-full max-w-full cursor-pointer items-center justify-start gap-2 overflow-hidden px-2 text-muted-foreground"><PaperclipIcon className="size-4 shrink-0" /><Tooltip delayDuration={400}><TooltipTrigger className="min-w-0 flex-1"><h4 className="w-full truncate text-left font-medium text-sm">{data.filename ?? "Unknown file"}</h4></TooltipTrigger><TooltipContent><div className="text-muted-foreground text-xs"><h4 className="max-w-[240px] overflow-hidden whitespace-normal break-words text-left font-semibold text-sm">{data.filename ?? "Unknown file"}</h4>{data.mediaType && <div>{data.mediaType}</div>}</div></TooltipContent></Tooltip></div>;
    $[21] = data.filename;
    $[22] = data.mediaType;
    $[23] = data.url;
    $[24] = mediaType;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  let t4;
  if ($[26] !== attachments || $[27] !== data.id) {
    t4 = () => attachments.remove(data.id);
    $[26] = attachments;
    $[27] = data.id;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  let t5;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <XIcon className="h-3 w-3" />;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] !== t4) {
    t6 = <Button aria-label="Remove attachment" className="-right-1.5 -top-1.5 absolute h-6 w-6 rounded-full opacity-0 group-hover:opacity-100" onClick={t4} size="icon" type="button" variant="outline">{t5}</Button>;
    $[30] = t4;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== data.id || $[33] !== props || $[34] !== t2 || $[35] !== t3 || $[36] !== t6) {
    t7 = <div className={t2} key={data.id} {...props}>{t3}{t6}</div>;
    $[32] = data.id;
    $[33] = props;
    $[34] = t2;
    $[35] = t3;
    $[36] = t6;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  return t7;
}
export type PromptInputAttachmentsProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  children: (attachment: FileUIPart & {
    id: string;
  }) => ReactNode;
};
export function PromptInputAttachments(t0) {
  const $ = _c(35);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const attachments = usePromptInputAttachments();
  const [height, setHeight] = useState(0);
  const contentRef = useRef(null);
  let t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const el = contentRef.current;
      if (!el) {
        return;
      }
      const ro = new ResizeObserver(() => {
        setHeight(el.getBoundingClientRect().height);
      });
      ro.observe(el);
      setHeight(el.getBoundingClientRect().height);
      return () => ro.disconnect();
    };
    t2 = [];
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const el_0 = contentRef.current;
      if (!el_0) {
        return;
      }
      setHeight(el_0.getBoundingClientRect().height);
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== attachments.files.length) {
    t4 = [attachments.files.length];
    $[7] = attachments.files.length;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useLayoutEffect(t3, t4);
  if (attachments.files.length === 0) {
    return null;
  }
  let t5;
  if ($[9] !== className) {
    t5 = cn("overflow-hidden transition-[height] duration-200 ease-out", className);
    $[9] = className;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = attachments.files.length ? height : 0;
  let t7;
  if ($[11] !== t6) {
    t7 = {
      height: t6
    };
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== attachments.files || $[14] !== children) {
    let t9;
    if ($[16] !== children) {
      t9 = file => <Fragment key={file.id}>{children(file)}</Fragment>;
      $[16] = children;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    t8 = attachments.files.filter(_temp4).map(t9);
    $[13] = attachments.files;
    $[14] = children;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <div className="flex flex-wrap gap-2">{t8}</div>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== attachments.files || $[21] !== children) {
    let t11;
    if ($[23] !== children) {
      t11 = file_0 => <Fragment key={file_0.id}>{children(file_0)}</Fragment>;
      $[23] = children;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    t10 = attachments.files.filter(_temp5).map(t11);
    $[20] = attachments.files;
    $[21] = children;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[25] !== t10) {
    t11 = <div className="flex flex-wrap gap-2">{t10}</div>;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t9) {
    t12 = <div className="space-y-2 py-1" ref={contentRef}>{t9}{t11}</div>;
    $[27] = t11;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== props || $[31] !== t12 || $[32] !== t5 || $[33] !== t7) {
    t13 = <InputGroupAddon align="block-start" aria-live="polite" className={t5} style={t7} {...props}>{t12}</InputGroupAddon>;
    $[30] = props;
    $[31] = t12;
    $[32] = t5;
    $[33] = t7;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}
function _temp5(f_0) {
  return f_0.mediaType?.startsWith("image/") && f_0.url;
}
function _temp4(f) {
  return !(f.mediaType?.startsWith("image/") && f.url);
}
export type PromptInputActionAddAttachmentsProps = ComponentProps<typeof DropdownMenuItem> & {
  label?: string;
};
export const PromptInputActionAddAttachments = t0 => {
  const $ = _c(10);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      label: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const label = t1 === undefined ? "Add photos or files" : t1;
  const attachments = usePromptInputAttachments();
  let t2;
  if ($[3] !== attachments) {
    t2 = () => {
      attachments.openFileDialog();
    };
    $[3] = attachments;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ImageIcon className="mr-2 size-4" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== label || $[7] !== props || $[8] !== t2) {
    t4 = <DropdownMenuItem {...props} onSelect={t2}>{t3} {label}</DropdownMenuItem>;
    $[6] = label;
    $[7] = props;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export interface PromptInputMessage {
  text?: string;
  files?: FileUIPart[];
}
export type PromptInputProps = Omit<HTMLAttributes<HTMLFormElement>, "onSubmit"> & {
  accept?: string; // e.g., "image/*" or leave undefined for any
  multiple?: boolean;
  // When true, accepts drops anywhere on document. Default false (opt-in).
  globalDrop?: boolean;
  // Render a hidden input with given name and keep it in sync for native form posts. Default false.
  syncHiddenInput?: boolean;
  // Minimal constraints
  maxFiles?: number;
  maxFileSize?: number; // bytes
  onError?: (err: {
    code: "max_files" | "max_file_size" | "accept";
    message: string;
  }) => void;
  onSubmit: (message: PromptInputMessage, event: FormEvent<HTMLFormElement>) => void | Promise<void>;
};
export const PromptInput = ({
  className,
  accept,
  multiple,
  globalDrop,
  syncHiddenInput,
  maxFiles,
  maxFileSize,
  onError,
  onSubmit,
  children,
  ...props
}: PromptInputProps) => {
  // Try to use a provider controller if present
  const controller = optional_usePromptInputController();
  const usingProvider = !!controller;

  // Refs
  const inputRef = useRef<HTMLInputElement | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  // Find nearest form to scope drag & drop
  useEffect(() => {
    const root = anchorRef.current?.closest("form");
    if (root instanceof HTMLFormElement) {
      formRef.current = root;
    }
  }, []);

  // ----- Local attachments (only used when no provider)
  const [items, setItems] = useState<(FileUIPart & {
    id: string;
  })[]>([]);
  const files = usingProvider ? controller.attachments.files : items;
  const openFileDialogLocal = useCallback(() => {
    inputRef.current?.click();
  }, []);
  const matchesAccept = useCallback((f: File) => {
    if (!accept || accept.trim() === "") {
      return true;
    }
    const patterns = accept.split(",").map(p => p.trim()).filter(Boolean);
    if (patterns.length === 0) {
      return true;
    }
    return patterns.some(pattern => {
      if (pattern.endsWith("/*")) {
        const prefix = pattern.slice(0, -1);
        return f.type.startsWith(prefix);
      }
      if (pattern.startsWith(".")) {
        return f.name.toLowerCase().endsWith(pattern.toLowerCase());
      }
      return f.type === pattern;
    });
  }, [accept]);
  const addLocal = useCallback((fileList: File[] | FileList) => {
    const incoming = Array.from(fileList);
    const accepted = incoming.filter(f_0 => matchesAccept(f_0));
    if (incoming.length && accepted.length === 0) {
      onError?.({
        code: "accept",
        message: "No files match the accepted types."
      });
      return;
    }
    const withinSize = (f_1: File) => maxFileSize ? f_1.size <= maxFileSize : true;
    const sized = accepted.filter(withinSize);
    if (accepted.length > 0 && sized.length === 0) {
      onError?.({
        code: "max_file_size",
        message: "All files exceed the maximum size."
      });
      return;
    }
    setItems(prev => {
      const capacity = typeof maxFiles === "number" ? Math.max(0, maxFiles - prev.length) : undefined;
      const capped = typeof capacity === "number" ? sized.slice(0, capacity) : sized;
      if (typeof capacity === "number" && sized.length > capacity) {
        onError?.({
          code: "max_files",
          message: "Too many files. Some were not added."
        });
      }
      const next: (FileUIPart & {
        id: string;
      })[] = [];
      for (const file of capped) {
        next.push({
          id: nanoid(),
          type: "file",
          url: URL.createObjectURL(file),
          mediaType: file.type,
          filename: file.name
        });
      }
      return prev.concat(next);
    });
  }, [matchesAccept, maxFiles, maxFileSize, onError]);
  const add = useCallback((files_0: File[] | FileList) => {
    if (usingProvider) {
      controller.attachments.add(files_0);
      return;
    }
    addLocal(files_0);
  }, [addLocal, controller, usingProvider]);
  const remove = useCallback((id: string) => {
    if (usingProvider) {
      controller.attachments.remove(id);
      return;
    }
    setItems(prev_0 => {
      const found = prev_0.find(file_0 => file_0.id === id);
      if (found?.url) {
        URL.revokeObjectURL(found.url);
      }
      return prev_0.filter(file_1 => file_1.id !== id);
    });
  }, [controller, usingProvider]);
  const clear = useCallback(() => {
    if (usingProvider) {
      controller.attachments.clear();
      return;
    }
    setItems(prev_1 => {
      for (const file_2 of prev_1) {
        if (file_2.url) {
          URL.revokeObjectURL(file_2.url);
        }
      }
      return [];
    });
  }, [controller, usingProvider]);
  const openFileDialog = useCallback(() => {
    if (usingProvider) {
      controller.attachments.openFileDialog();
      return;
    }
    openFileDialogLocal();
  }, [controller, openFileDialogLocal, usingProvider]);

  // Let provider know about our hidden file input so external menus can call openFileDialog()
  useEffect(() => {
    if (!usingProvider) {
      return;
    }
    controller.__registerFileInput(inputRef, () => inputRef.current?.click());
  }, [usingProvider, controller]);

  // Note: File input cannot be programmatically set for security reasons
  // The syncHiddenInput prop is no longer functional
  useEffect(() => {
    if (syncHiddenInput && inputRef.current && files.length === 0) {
      inputRef.current.value = "";
    }
  }, [files, syncHiddenInput]);

  // Attach drop handlers on nearest form and document (opt-in)
  useEffect(() => {
    const form = formRef.current;
    if (!form) {
      return;
    }
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
    };
    const onDrop = (e_0: DragEvent) => {
      if (e_0.dataTransfer?.types?.includes("Files")) {
        e_0.preventDefault();
      }
      if (e_0.dataTransfer?.files && e_0.dataTransfer.files.length > 0) {
        add(e_0.dataTransfer.files);
      }
    };
    form.addEventListener("dragover", onDragOver);
    form.addEventListener("drop", onDrop);
    return () => {
      form.removeEventListener("dragover", onDragOver);
      form.removeEventListener("drop", onDrop);
    };
  }, [add]);
  useEffect(() => {
    if (!globalDrop) {
      return;
    }
    const onDragOver_0 = (e_1: DragEvent) => {
      if (e_1.dataTransfer?.types?.includes("Files")) {
        e_1.preventDefault();
      }
    };
    const onDrop_0 = (e_2: DragEvent) => {
      if (e_2.dataTransfer?.types?.includes("Files")) {
        e_2.preventDefault();
      }
      if (e_2.dataTransfer?.files && e_2.dataTransfer.files.length > 0) {
        add(e_2.dataTransfer.files);
      }
    };
    document.addEventListener("dragover", onDragOver_0);
    document.addEventListener("drop", onDrop_0);
    return () => {
      document.removeEventListener("dragover", onDragOver_0);
      document.removeEventListener("drop", onDrop_0);
    };
  }, [add, globalDrop]);
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(() => {
    return () => {
      if (!usingProvider) {
        for (const f_2 of filesRef.current) {
          if (f_2.url) {
            URL.revokeObjectURL(f_2.url);
          }
        }
      }
    };
  }, [usingProvider]);
  const handleChange: ChangeEventHandler<HTMLInputElement> = event => {
    if (event.currentTarget.files) {
      add(event.currentTarget.files);
    }
  };
  const convertBlobUrlToDataUrl = async (url: string): Promise<string> => {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
  const ctx = useMemo<AttachmentsContext>(() => ({
    files: files.map(item => ({
      ...item,
      id: item.id
    })),
    add,
    remove,
    clear,
    openFileDialog,
    fileInputRef: inputRef
  }), [files, add, remove, clear, openFileDialog]);
  const handleSubmit: FormEventHandler<HTMLFormElement> = event_0 => {
    event_0.preventDefault();
    const form_0 = event_0.currentTarget;
    const text = usingProvider ? controller.textInput.value : (() => {
      const formData = new FormData(form_0);
      return formData.get("message") as string || "";
    })();

    // Reset form immediately after capturing text to avoid race condition
    // where user input during async blob conversion would be lost
    if (!usingProvider) {
      form_0.reset();
    }

    // Convert blob URLs to data URLs asynchronously
    void Promise.all(files.map(async ({
      id: _id,
      ...item_0
    }) => {
      if (item_0.url && item_0.url.startsWith("blob:")) {
        return {
          ...item_0,
          url: await convertBlobUrlToDataUrl(item_0.url)
        };
      }
      return item_0;
    })).then((convertedFiles: FileUIPart[]) => {
      try {
        const result = onSubmit({
          text,
          files: convertedFiles
        }, event_0);

        // Handle both sync and async onSubmit
        if (result instanceof Promise) {
          result.then(() => {
            clear();
            if (usingProvider) {
              controller.textInput.clear();
            }
          }).catch(() => {
            // Don't clear on error - user may want to retry
          });
        } else {
          // Sync function completed without throwing, clear attachments
          clear();
          if (usingProvider) {
            controller.textInput.clear();
          }
        }
      } catch {
        // Don't clear on error - user may want to retry
      }
    });
  };

  // Render with or without local provider
  const inner = <>
			<span aria-hidden="true" className="hidden" ref={anchorRef} />
			<input accept={accept} aria-label="Upload files" className="hidden" multiple={multiple} onChange={handleChange} ref={inputRef} title="Upload files" type="file" />
			<form className={cn("w-full", className)} onSubmit={handleSubmit} {...props}>
				<InputGroup>{children}</InputGroup>
			</form>
		</>;
  return usingProvider ? inner : <LocalAttachmentsContext value={ctx}>{inner}</LocalAttachmentsContext>;
};
export type PromptInputBodyProps = HTMLAttributes<HTMLDivElement>;
export const PromptInputBody = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("contents", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputTextareaProps = ComponentProps<typeof InputGroupTextarea>;
export const PromptInputTextarea = t0 => {
  const $ = _c(18);
  let className;
  let onChange;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      onChange,
      className,
      placeholder: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onChange;
    $[3] = props;
    $[4] = t1;
  } else {
    className = $[1];
    onChange = $[2];
    props = $[3];
    t1 = $[4];
  }
  const placeholder = t1 === undefined ? "What would you like to know?" : t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = optional_usePromptInputController();
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const controller = t2;
  const attachments = usePromptInputAttachments();
  const handleKeyDown = _temp6;
  let t3;
  if ($[6] !== attachments) {
    t3 = event => {
      const items = event.clipboardData?.items;
      if (!items) {
        return;
      }
      const files = [];
      for (const item of Array.from(items)) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
      if (files.length > 0) {
        event.preventDefault();
        attachments.add(files);
      }
    };
    $[6] = attachments;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handlePaste = t3;
  let t4;
  if ($[8] !== onChange) {
    t4 = controller ? {
      value: controller.textInput.value,
      onChange: e_0 => {
        controller.textInput.setInput(e_0.currentTarget.value);
        onChange?.(e_0);
      }
    } : {
      onChange
    };
    $[8] = onChange;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const controlledProps = t4;
  let t5;
  if ($[10] !== className) {
    t5 = cn("field-sizing-content max-h-48 min-h-16", className);
    $[10] = className;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== controlledProps || $[13] !== handlePaste || $[14] !== placeholder || $[15] !== props || $[16] !== t5) {
    t6 = <InputGroupTextarea className={t5} name="message" onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder={placeholder} {...props} {...controlledProps} />;
    $[12] = controlledProps;
    $[13] = handlePaste;
    $[14] = placeholder;
    $[15] = props;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
export type PromptInputToolbarProps = Omit<ComponentProps<typeof InputGroupAddon>, "align">;
export const PromptInputToolbar = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("justify-between gap-1 select-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <InputGroupAddon align="block-end" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>;
export const PromptInputTools = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex items-center gap-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputButtonProps = ComponentProps<typeof InputGroupButton>;
export const PromptInputButton = t0 => {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  let variant;
  if ($[0] !== t0) {
    const {
      variant: t2,
      className: t3,
      size,
      ...t4
    } = t0;
    className = t3;
    props = t4;
    variant = t2 === undefined ? "ghost" : t2;
    t1 = size ?? (Children.count(props.children) > 1 ? "sm" : "icon-sm");
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = variant;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    variant = $[4];
  }
  const newSize = t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn(className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== newSize || $[8] !== props || $[9] !== t2 || $[10] !== variant) {
    t3 = <InputGroupButton className={t2} size={newSize} type="button" variant={variant} {...props} />;
    $[7] = newSize;
    $[8] = props;
    $[9] = t2;
    $[10] = variant;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
export type PromptInputActionMenuProps = ComponentProps<typeof DropdownMenu>;
export const PromptInputActionMenu = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <DropdownMenu {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export type PromptInputActionMenuTriggerProps = PromptInputButtonProps;
export const PromptInputActionMenuTrigger = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== children) {
    t1 = children ?? <PlusIcon className="size-4" />;
    $[4] = children;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== className || $[7] !== props || $[8] !== t1) {
    t2 = <DropdownMenuTrigger asChild={true}><PromptInputButton className={className} {...props}>{t1}</PromptInputButton></DropdownMenuTrigger>;
    $[6] = className;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export type PromptInputActionMenuContentProps = ComponentProps<typeof DropdownMenuContent>;
export const PromptInputActionMenuContent = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DropdownMenuContent align="start" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputActionMenuItemProps = ComponentProps<typeof DropdownMenuItem>;
export const PromptInputActionMenuItem = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DropdownMenuItem className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};

// Note: Actions that perform side-effects (like opening a file dialog)
// are provided in opt-in modules (e.g., prompt-input-attachments).

export type PromptInputSubmitProps = ComponentProps<typeof InputGroupButton> & {
  status?: ChatStatus;
};
export const PromptInputSubmit = t0 => {
  const $ = _c(19);
  let children;
  let className;
  let props;
  let status;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      size: t2,
      status,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = status;
    $[5] = t1;
    $[6] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    status = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const variant = t1 === undefined ? "default" : t1;
  const size = t2 === undefined ? "icon-sm" : t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SendIcon className="size-4" />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let Icon = t3;
  if (status === "submitted") {
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Loader2Icon className="size-4 animate-spin" />;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    Icon = t4;
  } else {
    if (status === "streaming") {
      let t4;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <SquareIcon className="size-4" />;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      Icon = t4;
    } else {
      if (status === "error") {
        let t4;
        if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <XIcon className="size-4" />;
          $[10] = t4;
        } else {
          t4 = $[10];
        }
        Icon = t4;
      }
    }
  }
  let t4;
  if ($[11] !== className) {
    t4 = cn(className);
    $[11] = className;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = children ?? Icon;
  let t6;
  if ($[13] !== props || $[14] !== size || $[15] !== t4 || $[16] !== t5 || $[17] !== variant) {
    t6 = <InputGroupButton aria-label="Submit" className={t4} size={size} type="submit" variant={variant} {...props}>{t5}</InputGroupButton>;
    $[13] = props;
    $[14] = size;
    $[15] = t4;
    $[16] = t5;
    $[17] = variant;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
}
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  item: (index: number) => SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionResult {
  readonly length: number;
  item: (index: number) => SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}
interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}
interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}
declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}
export type PromptInputSpeechButtonProps = ComponentProps<typeof PromptInputButton> & {
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
  onTranscriptionChange?: (text: string) => void;
};
export const PromptInputSpeechButton = t0 => {
  const $ = _c(21);
  let className;
  let onTranscriptionChange;
  let props;
  let textareaRef;
  if ($[0] !== t0) {
    ({
      className,
      textareaRef,
      onTranscriptionChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onTranscriptionChange;
    $[3] = props;
    $[4] = textareaRef;
  } else {
    className = $[1];
    onTranscriptionChange = $[2];
    props = $[3];
    textareaRef = $[4];
  }
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const recognitionRef = useRef(null);
  let t1;
  let t2;
  if ($[5] !== onTranscriptionChange || $[6] !== textareaRef) {
    t1 = () => {
      if (typeof window !== "undefined" && ("SpeechRecognition" in window || "webkitSpeechRecognition" in window)) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const speechRecognition = new SpeechRecognition();
        speechRecognition.continuous = true;
        speechRecognition.interimResults = true;
        speechRecognition.lang = "en-US";
        speechRecognition.onstart = () => {
          setIsListening(true);
        };
        speechRecognition.onend = () => {
          setIsListening(false);
        };
        speechRecognition.onresult = event => {
          let finalTranscript = "";
          for (let i = 0; i < event.results.length; i++) {
            if (event.results[i].isFinal) {
              finalTranscript = finalTranscript + event.results[i][0].transcript;
            }
          }
          if (finalTranscript && textareaRef?.current) {
            const textarea = textareaRef.current;
            const currentValue = textarea.value;
            const newValue = currentValue + (currentValue ? " " : "") + finalTranscript;
            textarea.value = newValue;
            textarea.dispatchEvent(new Event("input", {
              bubbles: true
            }));
            onTranscriptionChange?.(newValue);
          }
        };
        speechRecognition.onerror = event_0 => {
          console.error("Speech recognition error:", event_0.error);
          setIsListening(false);
        };
        recognitionRef.current = speechRecognition;
        setRecognition(speechRecognition);
      }
      return () => {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      };
    };
    t2 = [textareaRef, onTranscriptionChange];
    $[5] = onTranscriptionChange;
    $[6] = textareaRef;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  if ($[9] !== isListening || $[10] !== recognition) {
    t3 = () => {
      if (!recognition) {
        return;
      }
      if (isListening) {
        recognition.stop();
      } else {
        recognition.start();
      }
    };
    $[9] = isListening;
    $[10] = recognition;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const toggleListening = t3;
  const t4 = isListening && "animate-pulse bg-accent text-accent-foreground";
  let t5;
  if ($[12] !== className || $[13] !== t4) {
    t5 = cn("relative transition-all duration-200", t4, className);
    $[12] = className;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = !recognition;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <MicIcon className="size-4" />;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== props || $[17] !== t5 || $[18] !== t6 || $[19] !== toggleListening) {
    t8 = <PromptInputButton className={t5} disabled={t6} onClick={toggleListening} {...props}>{t7}</PromptInputButton>;
    $[16] = props;
    $[17] = t5;
    $[18] = t6;
    $[19] = toggleListening;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
export type PromptInputModelSelectProps = ComponentProps<typeof Select>;
export const PromptInputModelSelect = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Select {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export type PromptInputModelSelectTriggerProps = ComponentProps<typeof SelectTrigger>;
export const PromptInputModelSelectTrigger = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("border-none bg-transparent font-medium text-muted-foreground shadow-none transition-colors", "hover:bg-accent hover:text-foreground [&[aria-expanded=\"true\"]]:bg-accent [&[aria-expanded=\"true\"]]:text-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectTrigger className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputModelSelectContentProps = ComponentProps<typeof SelectContent>;
export const PromptInputModelSelectContent = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectContent className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputModelSelectItemProps = ComponentProps<typeof SelectItem>;
export const PromptInputModelSelectItem = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectItem className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputModelSelectValueProps = ComponentProps<typeof SelectValue>;
export const PromptInputModelSelectValue = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectValue className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
function _temp6(e) {
  if (e.key === "Enter") {
    if (e.nativeEvent.isComposing) {
      return;
    }
    if (e.shiftKey) {
      return;
    }
    e.preventDefault();
    e.currentTarget.form?.requestSubmit();
  }
}